import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { MdCalendarToday, MdSchedule, MdPerson, MdInfo, MdCheckCircle, MdRadioButtonUnchecked, MdClose } from "react-icons/md";
import { getBookings, createBooking, updateBookingStatus } from "../../services/booking.service";
import { getEmployee } from "../../services/employee.service";
import { getServices } from "../../services/inventory.service";
import { toast } from "react-toastify";

const toYmd = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const sortTimes = (times = []) => {
  const toMinutes = (raw) => {
    const value = String(raw || "").trim();

    const ampm = value.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (ampm) {
      let hour = Number(ampm[1]);
      const minute = Number(ampm[2]);
      const period = ampm[3].toUpperCase();
      if (period === "AM" && hour === 12) hour = 0;
      if (period === "PM" && hour !== 12) hour += 12;
      return hour * 60 + minute;
    }

    const hhmm = value.match(/^(\d{1,2}):(\d{2})$/);
    if (hhmm) {
      const hour = Number(hhmm[1]);
      const minute = Number(hhmm[2]);
      return hour * 60 + minute;
    }

    return Number.MAX_SAFE_INTEGER;
  };

  return [...times].sort((a, b) => toMinutes(a) - toMinutes(b));
};

const toMinutes = (raw) => {
  const value = String(raw || "").trim();

  const ampm = value.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampm) {
    let hour = Number(ampm[1]);
    const minute = Number(ampm[2]);
    const period = ampm[3].toUpperCase();
    if (period === "AM" && hour === 12) hour = 0;
    if (period === "PM" && hour !== 12) hour += 12;
    return hour * 60 + minute;
  }

  const hhmm = value.match(/^(\d{1,2}):(\d{2})$/);
  if (hhmm) {
    const hour = Number(hhmm[1]);
    const minute = Number(hhmm[2]);
    return hour * 60 + minute;
  }

  return null;
};

const toHHMM = (minutes) => {
  if (typeof minutes !== "number" || Number.isNaN(minutes) || minutes < 0) return "";
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

const parseDurationMinutes = (durationValue, fallback = 60) => {
  if (durationValue === undefined || durationValue === null || durationValue === "") {
    return fallback;
  }

  const raw = String(durationValue).trim().toLowerCase();
  const numeric = Number(raw);
  if (!Number.isNaN(numeric) && numeric > 0) {
    return numeric;
  }

  const match = raw.match(/(\d+(?:\.\d+)?)/);
  if (!match) return fallback;

  const value = Number(match[1]);
  if (Number.isNaN(value) || value <= 0) return fallback;

  if (raw.includes("hour") || raw.includes("hr")) {
    return Math.round(value * 60);
  }

  return Math.round(value);
};

const buildSlotsForService = (service) => {
  const start = toMinutes(service?.openTime || "09:00");
  const end = toMinutes(service?.closeTime || "18:00");
  const duration = parseDurationMinutes(service?.serviceDuration, 60);
  const breakStart = toMinutes(service?.breakStartTime || "");
  const breakEnd = toMinutes(service?.breakEndTime || "");
  const hasValidBreak = breakStart !== null && breakEnd !== null && breakStart < breakEnd;

  if (start === null || end === null || duration <= 0 || start >= end) {
    return [];
  }

  const slots = [];
  let cursor = start;
  while (cursor < end) {
    const slotEnd = cursor + duration;
    const overlapsBreak = hasValidBreak && cursor < breakEnd && breakStart < slotEnd;
    if (!overlapsBreak) {
      slots.push(toHHMM(cursor));
    }
    cursor += duration;
  }

  return slots;
};

const isSlotOverlappingServiceBreak = (slotTime, service) => {
  if (!slotTime || !service) return false;

  const slotStart = toMinutes(slotTime);
  const breakStart = toMinutes(service?.breakStartTime || "");
  const breakEnd = toMinutes(service?.breakEndTime || "");
  const duration = parseDurationMinutes(service?.serviceDuration, 60);

  const hasValidBreak =
    slotStart !== null &&
    breakStart !== null &&
    breakEnd !== null &&
    breakStart < breakEnd;

  if (!hasValidBreak) return false;

  const slotEnd = slotStart + duration;
  return slotStart < breakEnd && breakStart < slotEnd;
};

const normalizePriceType = (priceType, hourlyRate = 0) => {
  const raw = String(priceType || "").trim().toLowerCase();
  if (raw.includes("hour")) return "hourly";

  const parsedRate = Number(hourlyRate || 0);
  if (!Number.isNaN(parsedRate) && parsedRate > 0) {
    return "hourly";
  }

  return "fixed";
};

export default function OnlineServiceBookingDashboardPage() {
  const { bussinessUuid } = useParams();

  const [bookings, setBookings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(toYmd(new Date()));
  const [manualAssignOpen, setManualAssignOpen] = useState(false);
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [manualAssignSlot, setManualAssignSlot] = useState("");
  const [manualAssignEmployee, setManualAssignEmployee] = useState(null);
  const [manualAssignableServices, setManualAssignableServices] = useState([]);
  const [manualAssignForm, setManualAssignForm] = useState({
    serviceId: "",
    name: "",
    mobile: "",
    email: "",
    paymentMode: "cash",
  });

  useEffect(() => {
    const loadData = async () => {
      if (!bussinessUuid) return;

      setLoading(true);
      try {
        const [bookingData, employeeResp, servicesResp] = await Promise.all([
          getBookings(bussinessUuid),
          getEmployee(bussinessUuid, { limit: 300 }),
          getServices(bussinessUuid, { all: "true" }),
        ]);

        setBookings(Array.isArray(bookingData) ? bookingData : []);

        const employeeRows =
          employeeResp?.data?.data ||
          employeeResp?.data ||
          [];

        setEmployees(Array.isArray(employeeRows) ? employeeRows : []);
        setServices(
          Array.isArray(servicesResp?.data?.data)
            ? servicesResp.data.data
            : Array.isArray(servicesResp?.data)
              ? servicesResp.data
              : []
        );
      } catch (error) {
        console.error("Failed to load booking dashboard data", error);
        setBookings([]);
        setEmployees([]);
        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [bussinessUuid]);

  const servicesById = useMemo(() => {
    const map = new Map();
    services.forEach((service) => {
      if (service?.serviceId) {
        map.set(service.serviceId, service);
      }
    });
    return map;
  }, [services]);

  const servicePricingSummary = useMemo(() => {
    const summary = { fixed: 0, hourly: 0 };
    services.forEach((service) => {
      const type = normalizePriceType(service?.pricingType, service?.hourlyRate);
      if (type === "hourly") {
        summary.hourly += 1;
      } else {
        summary.fixed += 1;
      }
    });
    return summary;
  }, [services]);

  const filteredServices = services;

  const filteredBookings = useMemo(
    () =>
      bookings.filter((booking) => {
        if (booking.date !== selectedDate) return false;
        return true;
      }),
    [bookings, selectedDate]
  );

  const activeBookings = useMemo(
    () => filteredBookings.filter((booking) => String(booking?.status || "").toLowerCase() !== "cancelled"),
    [filteredBookings]
  );

  const cancelledBookings = useMemo(
    () => filteredBookings.filter((booking) => String(booking?.status || "").toLowerCase() === "cancelled"),
    [filteredBookings]
  );

  const buildBusinessSlots = (serviceRows = [], stepMinutes = 60) => {
    const firstWithWindow = serviceRows.find(
      (service) => toMinutes(service?.openTime) !== null && toMinutes(service?.closeTime) !== null
    );

    if (!firstWithWindow) {
      return [];
    }

    const start = toMinutes(firstWithWindow.openTime);
    const end = toMinutes(firstWithWindow.closeTime);
    const breakStart = toMinutes(firstWithWindow.breakStartTime || "");
    const breakEnd = toMinutes(firstWithWindow.breakEndTime || "");
    const hasValidBreak = breakStart !== null && breakEnd !== null && breakStart < breakEnd;

    if (start === null || end === null || start >= end) {
      return [];
    }

    const slots = [];
    let cursor = start;
    while (cursor < end) {
      const overlapsBreak = hasValidBreak && cursor >= breakStart && cursor < breakEnd;
      if (!overlapsBreak) {
        slots.push(toHHMM(cursor));
      }
      cursor += stepMinutes;
    }

    return slots;
  };

  const slotTimes = useMemo(() => {
    const slotsFromBusinessWindow = buildBusinessSlots(filteredServices, 60);
    return sortTimes([...new Set(slotsFromBusinessWindow)]);
  }, [filteredServices]);

  const totalSlots = slotTimes.length;

  const employeeRows = useMemo(() => {
    const employeesFromMaster = (employees || []).map((emp) => ({
      id: emp.employeeID || emp.employeeId || emp._id,
      employeeBusinessId: emp.employeeID || emp.employeeId || "",
      mongoId: emp._id || "",
      name: emp.employeeName || emp.name || emp.fullName || "Unknown Employee",
    }));

    const employeesFromBookings = filteredBookings
      .map((booking) => ({
        id: booking.employeeId || `booking-${booking.employeeName}`,
        employeeBusinessId: booking.employeeId || "",
        mongoId: "",
        name: booking.employeeName || "Unassigned",
      }))
      .filter((item) => item.id || item.name);

    const merged = [...employeesFromMaster, ...employeesFromBookings].filter((item) => item.id && item.name);
    const unique = [];
    const seen = new Set();

    merged.forEach((item) => {
      if (seen.has(item.id)) return;
      seen.add(item.id);
      unique.push(item);
    });

    return unique;
  }, [employees, filteredBookings]);

  const bookingsByEmployeeIntervals = useMemo(() => {
    const map = new Map();

    activeBookings.forEach((booking) => {
      const employeeKey = booking.employeeId || `booking-${booking.employeeName}`;
      const bookingStart = toMinutes(booking.time);
      if (!employeeKey || bookingStart === null) return;

      const bookingService = servicesById.get(booking.serviceId);
      const bookingDuration = parseDurationMinutes(bookingService?.serviceDuration, 60);
      const bookingEnd = bookingStart + bookingDuration;

      if (!map.has(employeeKey)) {
        map.set(employeeKey, []);
      }

      map.get(employeeKey).push({
        start: bookingStart,
        end: bookingEnd,
        booking,
      });
    });

    return map;
  }, [activeBookings, servicesById]);

  const cancelledByEmployeeIntervals = useMemo(() => {
    const map = new Map();

    cancelledBookings.forEach((booking) => {
      const employeeKey = booking.employeeId || `booking-${booking.employeeName}`;
      const bookingStart = toMinutes(booking.time);
      if (!employeeKey || bookingStart === null) return;

      const bookingService = servicesById.get(booking.serviceId);
      const bookingDuration = parseDurationMinutes(bookingService?.serviceDuration, 60);
      const bookingEnd = bookingStart + bookingDuration;

      if (!map.has(employeeKey)) {
        map.set(employeeKey, []);
      }

      map.get(employeeKey).push({
        start: bookingStart,
        end: bookingEnd,
        booking,
      });
    });

    return map;
  }, [cancelledBookings, servicesById]);

  const getHourRange = (slot) => {
    const start = toMinutes(slot);
    if (start === null) return null;
    return { start, end: start + 60 };
  };

  const getBookingsInHour = (employeeId, slot) => {
    const range = getHourRange(slot);
    if (!range) return [];
    const intervals = bookingsByEmployeeIntervals.get(employeeId) || [];
    return intervals
      .filter(({ start, end }) => start < range.end && range.start < end)
      .map(({ booking }) => booking);
  };

  const getCancelledBookingsInHour = (employeeId, slot) => {
    const range = getHourRange(slot);
    if (!range) return [];
    const intervals = cancelledByEmployeeIntervals.get(employeeId) || [];
    return intervals
      .filter(({ start, end }) => start < range.end && range.start < end)
      .map(({ booking }) => booking);
  };

  const getServiceStartTimesForHour = (service, slot) => {
    const range = getHourRange(slot);
    if (!range) return [];

    const openMinutes = toMinutes(service?.openTime || "");
    const closeMinutes = toMinutes(service?.closeTime || "");
    const duration = parseDurationMinutes(service?.serviceDuration, 60);

    if (openMinutes === null || closeMinutes === null || duration <= 0 || openMinutes >= closeMinutes) {
      return [];
    }

    const startTimes = [];
    let cursor = openMinutes;
    while (cursor + duration <= closeMinutes) {
      const slotTime = toHHMM(cursor);
      const inHour = cursor >= range.start && cursor < range.end;
      if (inHour && !isSlotOverlappingServiceBreak(slotTime, service)) {
        startTimes.push(slotTime);
      }
      cursor += duration;
    }

    return startTimes;
  };

  const getPossibleStartTimesForEmployeeHour = (employee, slot) => {
    const mappedServices = getMappedServicesForEmployee(employee);
    const set = new Set();
    mappedServices.forEach((service) => {
      getServiceStartTimesForHour(service, slot).forEach((time) => set.add(time));
    });
    return sortTimes([...set]);
  };

  const getHourBookingSummary = (employee, slot) => {
    const possibleStartTimes = getPossibleStartTimesForEmployeeHour(employee, slot);
    const intervals = bookingsByEmployeeIntervals.get(employee.id) || [];
    const bookedStartTimes = possibleStartTimes.filter((time) => {
      const minute = toMinutes(time);
      if (minute === null) return false;
      return intervals.some(({ start, end }) => start <= minute && minute < end);
    });

    return {
      possibleStartTimes,
      totalSlots: possibleStartTimes.length,
      bookedSlots: bookedStartTimes.length,
      freeSlots: Math.max(0, possibleStartTimes.length - bookedStartTimes.length),
      hasAnyBooking: getBookingsInHour(employee.id, slot).length > 0,
    };
  };

  const servicesInViewCount = filteredServices.length;

  const isServiceSlotStartAllowed = (service, slot) => {
    const slotStart = toMinutes(slot);
    const openMinutes = toMinutes(service?.openTime || "");
    const closeMinutes = toMinutes(service?.closeTime || "");
    if (slotStart === null || openMinutes === null || closeMinutes === null) {
      return false;
    }

    const duration = parseDurationMinutes(service?.serviceDuration, 60);
    const slotEnd = slotStart + duration;

    if (slotStart < openMinutes || slotEnd > closeMinutes) {
      return false;
    }

    if (isSlotOverlappingServiceBreak(slot, service)) {
      return false;
    }

    return true;
  };

  const isPastSlotForSelectedDate = (slot) => {
    const slotMinutes = toMinutes(slot);
    if (slotMinutes === null || !selectedDate) return false;

    const today = new Date();
    const todayYmd = toYmd(today);
    if (selectedDate !== todayYmd) {
      return false;
    }

    const nowMinutes = today.getHours() * 60 + today.getMinutes();
    return slotMinutes <= nowMinutes;
  };

  const isEmployeeMappedToService = (employee, service) => {
    if (!employee || !service) return false;

    const serviceEmployeeIds = (service?.employees || [])
      .map((entry) => String(entry?.employeeId || "").trim())
      .filter(Boolean);

    if (!serviceEmployeeIds.length) return false;

    const candidates = [
      employee?.employeeBusinessId,
      employee?.id,
      employee?.mongoId,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    return candidates.some((candidate) => serviceEmployeeIds.includes(candidate));
  };

  const getMappedServicesForEmployee = (employee) => {
    if (!employee) return [];
    return filteredServices.filter((service) => isEmployeeMappedToService(employee, service));
  };

  const getAssignableServicesForEmployeeAndSlot = (employee, slot) => {
    return getMappedServicesForEmployee(employee).filter((service) =>
      isServiceSlotStartAllowed(service, slot)
    );
  };

  const getServiceAmount = (service) => {
    if (!service) return 0;
    const type = normalizePriceType(service?.pricingType, service?.hourlyRate);
    if (type === "hourly") {
      const durationHours = parseDurationMinutes(service?.serviceDuration, 60) / 60;
      return Number((Number(service?.hourlyRate || 0) * durationHours).toFixed(2));
    }
    return Number(service?.salePrice || 0);
  };

  const reloadBookings = async () => {
    const bookingData = await getBookings(bussinessUuid);
    setBookings(Array.isArray(bookingData) ? bookingData : []);
  };

  const openManualAssignModal = (employee, slot) => {
    if (isPastSlotForSelectedDate(slot)) {
      toast.error("Cannot assign a past time slot.");
      return;
    }

    const mappedServices = getMappedServicesForEmployee(employee);
    if (!mappedServices.length) {
      toast.error("This employee is not mapped to any assignable service for this slot.");
      return;
    }

    const summary = getHourBookingSummary(employee, slot);
    const nextAssignableTimes = summary.possibleStartTimes.filter((time) => {
      const minute = toMinutes(time);
      if (minute === null) return false;
      const intervals = bookingsByEmployeeIntervals.get(employee.id) || [];
      return !intervals.some(({ start, end }) => start <= minute && minute < end);
    });

    if (nextAssignableTimes.length === 0) {
      toast.error("No free slots left in this 1-hour block for this employee.");
      return;
    }

    const defaultServiceId = mappedServices[0]?.serviceId || "";
    setManualAssignEmployee(employee);
    setManualAssignSlot(nextAssignableTimes[0]);
    setManualAssignableServices(mappedServices);
    setManualAssignForm({
      serviceId: defaultServiceId,
      name: "",
      mobile: "",
      email: "",
      paymentMode: "cash",
    });
    setManualAssignOpen(true);
  };

  const closeManualAssignModal = () => {
    if (assignSubmitting) return;
    setManualAssignOpen(false);
    setManualAssignableServices([]);
  };

  const handleManualAssignSubmit = async () => {
    if (!manualAssignEmployee?.id || !manualAssignSlot || !selectedDate) {
      toast.error("Missing employee, slot, or date.");
      return;
    }

    if (isPastSlotForSelectedDate(manualAssignSlot)) {
      toast.error("Cannot assign a past time slot.");
      return;
    }

    if (!manualAssignForm.serviceId || !manualAssignForm.name || !manualAssignForm.mobile) {
      toast.error("Service, customer name, and mobile are required.");
      return;
    }

    const selectedService = manualAssignableServices.find((service) => service.serviceId === manualAssignForm.serviceId);
    if (!selectedService) {
      toast.error("Selected service not found.");
      return;
    }

    setAssignSubmitting(true);
    try {
      const bookingPayload = {
        serviceId: selectedService.serviceId,
        date: selectedDate,
        time: manualAssignSlot,
        employeeId: manualAssignEmployee.id,
        employeeName: manualAssignEmployee.name,
        name: manualAssignForm.name,
        email: manualAssignForm.email,
        mobile: manualAssignForm.mobile,
        amount: getServiceAmount(selectedService),
        paymentMode: manualAssignForm.paymentMode || "cash",
      };

      const created = await createBooking(bussinessUuid, bookingPayload);
      const bookingId = created?.booking?._id;
      if (!bookingId) {
        throw new Error("Booking creation failed.");
      }

      await updateBookingStatus(bussinessUuid, bookingId, "Confirmed");
      await reloadBookings();
      toast.success("Service task assigned to employee successfully.");
      setManualAssignOpen(false);
    } catch (error) {
      console.error("Manual booking assignment failed", error);
      toast.error(error?.response?.data?.message || "Failed to assign service task.");
    } finally {
      setAssignSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f2f6fc] px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header Card */}
        <div className="rounded-2xl bg-white p-5 shadow-sm border border-[#e6edf5] sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#0a2a44] flex items-center gap-2">
                <MdCalendarToday className="text-[#1a5fb0]" size={24} />
                Booking Dashboard
              </h1>
              <p className="mt-1 text-sm text-[#3a5a7a]">
                Slots are business-timing based. Pricing mode is applied per service.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex items-center gap-2 bg-[#f2f7fd] px-3 py-2 rounded-xl border border-[#dae6f2] text-xs font-semibold text-[#1f4e7a]">
                <span className="rounded-full bg-[#e2edfc] px-2 py-0.5">Fixed: {servicePricingSummary.fixed}</span>
                <span className="rounded-full bg-[#d9f3e7] px-2 py-0.5 text-[#166534]">Hourly: {servicePricingSummary.hourly}</span>
              </div>

              <div className="flex items-center gap-2 bg-[#f8fcff] border border-[#dae6f2] rounded-xl px-3 py-1.5">
                <MdCalendarToday className="text-[#1a5fb0]" size={16} />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(event) => setSelectedDate(event.target.value)}
                  className="bg-transparent border-none outline-none text-sm font-medium text-[#0a2a44] py-1 px-1 w-36"
                />
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-[#f7fbff] rounded-xl border border-[#e2ebf5] px-4 py-3 flex items-center gap-3">
              <div className="bg-[#dce8f7] p-2 rounded-full">
                <MdInfo className="text-[#1a5fb0]" size={18} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-[#3f6385]">Pricing Scope</p>
                <p className="text-base font-bold text-[#0a2a44]">
                  Service-wise
                </p>
              </div>
            </div>
            <div className="bg-[#f7fbff] rounded-xl border border-[#e2ebf5] px-4 py-3 flex items-center gap-3">
              <div className="bg-[#dce8f7] p-2 rounded-full">
                <MdSchedule className="text-[#1a5fb0]" size={18} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-[#3f6385]">Total Slots</p>
                <p className="text-base font-bold text-[#0a2a44]">{totalSlots}</p>
                <p className="text-[10px] text-[#517694]">Based on open/close time</p>
              </div>
            </div>
            <div className="bg-[#f7fbff] rounded-xl border border-[#e2ebf5] px-4 py-3 flex items-center gap-3">
              <div className="bg-[#dce8f7] p-2 rounded-full">
                <MdPerson className="text-[#1a5fb0]" size={18} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-[#3f6385]">Services In View</p>
                <p className="text-base font-bold text-[#0a2a44]">{servicesInViewCount}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Table Card */}
        <div className="rounded-2xl bg-white p-4 shadow-sm border border-[#e6edf5] sm:p-5">
          {loading ? (
            <p className="py-8 text-center text-[#3a5a7a]">Loading booking dashboard...</p>
          ) : slotTimes.length === 0 ? (
            <p className="py-8 text-center text-[#3a5a7a]">No slot details found for this pricing view and selected date.</p>
          ) : employeeRows.length === 0 ? (
            <p className="py-8 text-center text-[#3a5a7a]">No employees found for this business.</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-[#eaf0f7]">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 min-w-[200px] border-b border-[#dce6f0] bg-[#f8fcff] px-4 py-3 text-left font-semibold text-[#1a3a5a]">
                      <span className="flex items-center gap-2">
                        <MdPerson className="text-[#1a5fb0]" size={16} />
                        Employee Name
                      </span>
                    </th>
                    {slotTimes.map((slot) => (
                      <th
                        key={slot}
                        className="min-w-[160px] border-b border-[#dce6f0] bg-[#f8fcff] px-3 py-3 text-center font-semibold text-[#1a3a5a]"
                      >
                        <div className="flex items-center justify-center gap-1.5">
                          <MdSchedule className="text-[#1a5fb0]" size={14} />
                          <span>{slot} - {toHHMM((toMinutes(slot) || 0) + 60)}</span>
                        </div>
                        <p className="mt-1 text-[10px] font-normal text-[#4f7497]">1 Hour Bucket</p>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {employeeRows.map((employee) => (
                    <tr key={employee.id} className="hover:bg-[#f8fcff] transition-colors">
                      <td className="sticky left-0 z-10 border-b border-[#e2ebf4] bg-white px-4 py-3 font-semibold text-[#0a2a44]">
                        <span className="flex items-center gap-2">
                          <MdPerson className="text-[#1a5fb0]" size={16} />
                          {employee.name}
                        </span>
                      </td>

                      {slotTimes.map((slot) => {
                        const bookingsInHour = getBookingsInHour(employee.id, slot);
                        const cancelledInHour = getCancelledBookingsInHour(employee.id, slot);
                        const summary = getHourBookingSummary(employee, slot);
                        const isBooked = bookingsInHour.length > 0;

                        if (isBooked) {
                          const booking = bookingsInHour[0];
                          const priceType = booking
                            ? normalizePriceType(
                                booking.servicePriceType || booking.pricingType,
                                servicesById.get(booking.serviceId)?.hourlyRate
                              )
                            : "fixed";

                          return (
                            <td
                              key={`${employee.id}-${slot}`}
                              className="border-b border-[#e2ebf4] px-3 py-3 align-top bg-[#f0fdf4]"
                            >
                              <div className="space-y-1">
                                <p className="font-semibold text-[#166534] flex items-center gap-1">
                                  <MdCheckCircle className="text-[#22c55e]" size={14} />
                                  Booked
                                </p>
                                <p className="text-xs text-[#15803d] font-medium">{booking.serviceName || "Service"}</p>
                                <p className="text-xs font-medium text-[#166534]">
                                  {priceType === "hourly" ? "Hourly" : "Fixed"}: Rs. {Number(booking.amount || 0).toFixed(2)}
                                </p>
                                <p className="text-[10px] text-[#166534]">Booked Slots: {summary.bookedSlots} | Free Slots: {summary.freeSlots}</p>
                                {bookingsInHour.length > 1 && (
                                  <p className="text-[10px] text-[#166534]">+{bookingsInHour.length - 1} more booking(s) in this hour</p>
                                )}
                              </div>
                            </td>
                          );
                        }

                        return (
                          <td
                            key={`${employee.id}-${slot}`}
                            className="border-b border-[#e2ebf4] px-3 py-3 align-top bg-[#fef2f2]"
                          >
                            <div className="space-y-1">
                              {(() => {
                                const mappedServices = getMappedServicesForEmployee(employee);
                                const isPastSlot = isPastSlotForSelectedDate(slot);
                                const canAssign = mappedServices.length > 0 && !isPastSlot && summary.freeSlots > 0;

                                return (
                                  <>
                              <p className="font-semibold text-[#991b1b] flex items-center gap-1">
                                <MdRadioButtonUnchecked className="text-[#ef4444]" size={14} />
                                Not Booked
                              </p>
                              <p className="text-xs text-[#b91c1c]">
                                {isPastSlot
                                  ? "Past slot. Assignment disabled."
                                  : mappedServices.length === 0
                                    ? "No mapped service can start at this slot."
                                    : summary.freeSlots === 0
                                      ? "No free slot left in this 1-hour block."
                                      : "No booking for this slot"}
                              </p>
                              <p className="text-[10px] text-[#7f1d1d]">Booked Slots: {summary.bookedSlots} | Free Slots: {summary.freeSlots}</p>
                              {cancelledInHour.length > 0 && (
                                <p className="text-[10px] text-[#7f1d1d]">
                                  {cancelledInHour.length} cancelled slot(s) available for reassignment
                                </p>
                              )}
                              {mappedServices.length > 0 && !isPastSlot && (
                                <p className="text-[10px] text-[#7f1d1d]">
                                  Services: {mappedServices.slice(0, 2).map((svc) => svc.serviceName).join(", ")}
                                  {mappedServices.length > 2 ? ` +${mappedServices.length - 2} more` : ""}
                                </p>
                              )}
                              <button
                                type="button"
                                onClick={() => openManualAssignModal(employee, slot)}
                                disabled={!canAssign}
                                className="mt-1 rounded-md bg-[#1a5fb0] px-2 py-1 text-[11px] font-semibold text-white hover:bg-[#164f92] disabled:cursor-not-allowed disabled:bg-[#8ea4bf]"
                              >
                                Assign Service
                              </button>
                                  </>
                                );
                              })()}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {!loading && slotTimes.length > 0 && employeeRows.length > 0 && (
            <div className="mt-3 flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5 text-[#166534]">
                <span className="inline-block w-3 h-3 rounded bg-[#f0fdf4] border border-[#86efac]"></span>
                Booked
              </span>
              <span className="flex items-center gap-1.5 text-[#991b1b]">
                <span className="inline-block w-3 h-3 rounded bg-[#fef2f2] border border-[#fca5a5]"></span>
                Not Booked
              </span>
            </div>
          )}
        </div>

        {manualAssignOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl border border-[#e2ebf4]">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-bold text-[#0a2a44]">Manual Service Assignment</h3>
                <button
                  type="button"
                  onClick={closeManualAssignModal}
                  className="rounded-md p-1 text-[#376089] hover:bg-[#eef4fb]"
                >
                  <MdClose size={18} />
                </button>
              </div>

              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg bg-[#f7fbff] border border-[#e2ebf5] p-2">
                    <p className="text-[11px] uppercase text-[#4f7497]">Employee</p>
                    <p className="font-semibold text-[#0a2a44]">{manualAssignEmployee?.name || "-"}</p>
                  </div>
                  <div className="rounded-lg bg-[#f7fbff] border border-[#e2ebf5] p-2">
                    <p className="text-[11px] uppercase text-[#4f7497]">Slot</p>
                    <p className="font-semibold text-[#0a2a44]">{selectedDate} | {manualAssignSlot}</p>
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#3f6385]">Service</label>
                  <select
                    value={manualAssignForm.serviceId}
                    onChange={(event) =>
                      setManualAssignForm((prev) => ({ ...prev, serviceId: event.target.value }))
                    }
                    className="w-full rounded-lg border border-[#d4e1ef] px-3 py-2 outline-none focus:border-[#1a5fb0]"
                  >
                    <option value="">Select service</option>
                    {manualAssignableServices.map((service) => (
                      <option key={service.serviceId} value={service.serviceId}>
                        {service.serviceName} ({normalizePriceType(service.pricingType, service.hourlyRate) === "hourly" ? "Hourly" : "Fixed"})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#3f6385]">Customer Name</label>
                    <input
                      type="text"
                      value={manualAssignForm.name}
                      onChange={(event) =>
                        setManualAssignForm((prev) => ({ ...prev, name: event.target.value }))
                      }
                      className="w-full rounded-lg border border-[#d4e1ef] px-3 py-2 outline-none focus:border-[#1a5fb0]"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#3f6385]">Mobile</label>
                    <input
                      type="text"
                      value={manualAssignForm.mobile}
                      onChange={(event) =>
                        setManualAssignForm((prev) => ({ ...prev, mobile: event.target.value }))
                      }
                      className="w-full rounded-lg border border-[#d4e1ef] px-3 py-2 outline-none focus:border-[#1a5fb0]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#3f6385]">Email</label>
                    <input
                      type="email"
                      value={manualAssignForm.email}
                      onChange={(event) =>
                        setManualAssignForm((prev) => ({ ...prev, email: event.target.value }))
                      }
                      className="w-full rounded-lg border border-[#d4e1ef] px-3 py-2 outline-none focus:border-[#1a5fb0]"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#3f6385]">Payment Mode</label>
                    <select
                      value={manualAssignForm.paymentMode}
                      onChange={(event) =>
                        setManualAssignForm((prev) => ({ ...prev, paymentMode: event.target.value }))
                      }
                      className="w-full rounded-lg border border-[#d4e1ef] px-3 py-2 outline-none focus:border-[#1a5fb0]"
                    >
                      <option value="cash">Cash</option>
                      <option value="upi">UPI</option>
                      <option value="card">Card</option>
                      <option value="online">Online</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeManualAssignModal}
                  className="rounded-lg border border-[#cfdceb] px-4 py-2 text-sm font-semibold text-[#2f5d88] hover:bg-[#f2f7fd]"
                  disabled={assignSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleManualAssignSubmit}
                  className="rounded-lg bg-[#1a5fb0] px-4 py-2 text-sm font-semibold text-white hover:bg-[#164f92] disabled:opacity-60"
                  disabled={assignSubmitting}
                >
                  {assignSubmitting ? "Assigning..." : "Assign Task"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}